'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { formatDate } from '@/utils/dateUtils';

interface Photo {
  id: string;
  src: string;
  alt: string;
  category: string;
  location: string;
  date: string;
  title: string;
  description: string;
}

interface PhotoGalleryProps {
  photos: Photo[];
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos }) => {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const openLightbox = (photo: Photo, index: number) => {
    setSelectedPhoto(photo);
    setCurrentIndex(index);
  };

  const closeLightbox = () => setSelectedPhoto(null);

  const goToNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % photos.length;
    setCurrentIndex(nextIndex);
    setSelectedPhoto(photos[nextIndex]);
  }, [currentIndex, photos]);

  const goToPrevious = useCallback(() => {
    const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
    setCurrentIndex(prevIndex);
    setSelectedPhoto(photos[prevIndex]);
  }, [currentIndex, photos]);

  // Keyboard navigation
  useEffect(() => {
    if (!selectedPhoto) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedPhoto, goToNext, goToPrevious]);

  return (
    <>
      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="group relative bg-card rounded-xl overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 cursor-pointer"
            onClick={() => openLightbox(photo, index)}
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <AppImage
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h4 className="font-heading font-semibold text-sm sm:text-base mb-1 line-clamp-1">{photo.title}</h4>
                <div className="flex items-center gap-3 text-xs opacity-90">
                  <span className="flex items-center gap-1">
                    <Icon name="MapPinIcon" size={12} />
                    {photo.location}
                  </span>
                  {photo.date && (
                    <span className="flex items-center gap-1">
                      <Icon name="CalendarIcon" size={11} />
                      {formatDate(photo.date)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="absolute top-2 right-2 bg-primary/90 text-primary-foreground px-2 py-0.5 rounded-full text-xs font-semibold backdrop-blur-sm">
              {photo.category}
            </div>
            {/* Click hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={closeLightbox}
        >
          {/* Modal Content — stop click from closing */}
          <div
            className="relative bg-card rounded-2xl overflow-hidden shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-10 w-8 h-8 bg-foreground/70 hover:bg-foreground/90 rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label="Close"
            >
              <Icon name="XMarkIcon" size={18} className="text-white" />
            </button>

            {/* Image */}
            <div className="relative bg-muted overflow-hidden" style={{ maxHeight: '55vh' }}>
              <AppImage
                key={selectedPhoto.id}
                src={selectedPhoto.src}
                alt={selectedPhoto.alt}
                className="w-full h-full object-contain"
                style={{ maxHeight: '55vh' }}
              />

              {/* Prev / Next */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/75 rounded-full flex items-center justify-center transition-colors duration-200"
                    aria-label="Previous"
                  >
                    <Icon name="ChevronLeftIcon" size={20} className="text-white" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/75 rounded-full flex items-center justify-center transition-colors duration-200"
                    aria-label="Next"
                  >
                    <Icon name="ChevronRightIcon" size={20} className="text-white" />
                  </button>
                  {/* Counter */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    {currentIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>

            {/* Info Panel */}
            <div className="p-4 sm:p-5 overflow-y-auto">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-base sm:text-lg font-heading font-bold text-foreground leading-snug">
                  {selectedPhoto.title}
                </h3>
                <span className="flex-shrink-0 bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-semibold">
                  {selectedPhoto.category}
                </span>
              </div>

              {selectedPhoto.description && (
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  {selectedPhoto.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-xs text-muted-foreground border-t border-border pt-3">
                {selectedPhoto.location && (
                  <span className="flex items-center gap-1.5">
                    <Icon name="MapPinIcon" size={13} className="text-primary" />
                    {selectedPhoto.location}
                  </span>
                )}
                {selectedPhoto.date && (
                  <span className="flex items-center gap-1.5">
                    <Icon name="CalendarIcon" size={13} className="text-primary" />
                    {formatDate(selectedPhoto.date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;